import { useState } from 'react'
import { CommunicationIcon } from '@forwardflow/assets/svg/CommunicationIcon'
import { LeadSourceIcon } from '@forwardflow/assets/svg/LeadSourceIcon'
import { OverviewIcon } from '@forwardflow/assets/svg/OverviewIcon'
import { TeamPerformanceIcon } from '@forwardflow/assets/svg/TeamPerformanceIcon'

interface Tab {
   key: string
   label: string
   icon: React.ReactNode
}

interface DashboardTabsProps {
   onTabChange?: (key: string) => void
   activeTab: string
}

export const DashboardTabs = ({
   onTabChange,
   activeTab,
}: DashboardTabsProps) => {
   const [menuOpen, setMenuOpen] = useState(false)
   const tabs: Tab[] = [
      {
         key: 'overview',
         label: 'Overview',
         icon: <OverviewIcon />,
      },
      {
         key: 'lead-source',
         label: 'Lead Source',
         icon: <LeadSourceIcon />,
      },
      {
         key: 'communication',
         label: 'Communication',
         icon: <CommunicationIcon />,
      },
      {
         key: 'team-performance',
         label: 'Team Performance',
         icon: <TeamPerformanceIcon />,
      },
   ]

   return (
      <div className="flex w-full">
         {/* Desktop Tabs */}
         <div className="hidden overflow-x-auto rounded-lg sm:flex bg-white/5">
            {tabs.map((tab) => (
               <button
                  key={tab.key}
                  onClick={() => onTabChange?.(tab.key)}
                  className={`
                     flex items-center gap-2
                     px-3 py-2 sm:px-6 
                     text-xs sm:text-sm font-medium
                     transition-all duration-200 relative h-10
                     whitespace-nowrap
                     ${
                        activeTab === tab.key
                           ? 'text-white bg-white/5'
                           : 'text-white/50 hover:text-gray-200'
                     }
                  `}
               >
                  <span
                     className={`text-base sm:text-lg ${
                        activeTab === tab.key ? 'text-white' : 'text-gray-400'
                     }`}
                  >
                     {tab.icon}
                  </span>
                  <span>{tab.label}</span>
               </button>
            ))}
         </div>
         {/* Mobile Hamburger */}
         <div className="relative w-full sm:hidden">
            <button
               className="flex items-center justify-between w-full gap-2 px-4 py-2 text-white rounded-lg bg-white/10"
               onClick={() => setMenuOpen((open) => !open)}
            >
               <span className="flex items-center gap-1">
                  {tabs.find((tab) => tab.key === activeTab)?.icon}
                  <span className="ml-2">
                     {tabs.find((tab) => tab.key === activeTab)?.label}
                  </span>
               </span>
               {/* Hamburger Icon */}
               <svg width="24" height="24" fill="none" stroke="currentColor">
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     strokeWidth="2"
                     d="M4 6h16M4 12h16M4 18h16"
                  />
               </svg>
            </button>
            {menuOpen && (
               <div className="absolute left-0 right-0 z-10 mt-2 overflow-hidden bg-gray-800 rounded-lg shadow-lg">
                  {tabs.map((tab) => (
                     <button
                        key={tab.key}
                        onClick={() => {
                           onTabChange?.(tab.key)
                           setMenuOpen(false)
                        }}
                        className={`
                           flex items-center gap-2 w-full px-4 py-2
                           text-sm font-medium
                           ${
                              activeTab === tab.key
                                 ? 'text-white bg-white/20'
                                 : 'text-white/70 hover:bg-white/5'
                           }
                        `}
                     >
                        <span className="text-base">{tab.icon}</span>
                        <span>{tab.label}</span>
                     </button>
                  ))}
               </div>
            )}
         </div>
      </div>
   )
}
